import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Sparkles, Trash2, Printer, Send, CheckCircle2, XCircle, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import ActivityPanel from "@/components/ActivityPanel";

const WORKFLOW_ACTIONS = {
  open:        [{ id: "submit",  label: "Submit for Review", icon: Send,        variant: "default" }],
  in_progress: [
    { id: "submit",  label: "Submit for Review", icon: Send,        variant: "default" },
    { id: "close",   label: "Close",             icon: CheckCircle2, variant: "outline" },
  ],
  review:      [
    { id: "approve", label: "Approve",       icon: CheckCircle2, variant: "default" },
    { id: "reject",  label: "Send back",     icon: XCircle,      variant: "outline" },
  ],
  approved:    [
    { id: "publish", label: "Publish / Close", icon: Upload,     variant: "default" },
    { id: "revise",  label: "Revise",         icon: RotateCcw,   variant: "outline" },
  ],
  closed:      [{ id: "revise", label: "Reopen for Revision", icon: RotateCcw, variant: "outline" }],
  archived:    [],
};

const STATUS = ["open", "in_progress", "closed", "archived"];
const PRIORITY = ["low", "medium", "high", "critical"];

export default function RecordDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rec, setRec] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get(`/records/${id}`).then(async (r) => {
      setRec(r.data);
      const c = await api.get(`/module-config/${r.data.module_id}`);
      setCfg(c.data);
    }).catch(() => {
      toast.error("Record not found");
      nav("/app/modules");
    });
  }, [id, nav]);

  const recordType = useMemo(
    () => cfg?.record_types.find((t) => t.id === rec?.record_type),
    [cfg, rec],
  );

  const update = (k, v) => { setRec({ ...rec, [k]: v }); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/records/${id}`, {
        title: rec.title,
        description: rec.description,
        status: rec.status,
        priority: rec.priority,
        assignee: rec.assignee || null,
        due_date: rec.due_date || null,
      });
      setRec(data);
      setDirty(false);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    if (!rec.title.trim()) return toast.error("Set a title first");
    setGenerating(true);
    const context = rec.description ? `Existing notes: ${rec.description}` : "";
    try {
      const token = localStorage.getItem("ruleai_token");
      const res = await fetch(`${API}/records/ai-assist`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          module_id: rec.module_id,
          record_type: rec.record_type,
          title: rec.title,
          context,
        }),
      });
      if (!res.ok || !res.body) throw new Error("gen failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      setRec((r) => ({ ...r, description: "" }));
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setRec((r) => ({ ...r, description: acc }));
      }
      setDirty(true);
    } catch {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const del = async () => {
    await api.delete(`/records/${id}`);
    toast.success("Deleted");
    nav(`/app/modules/${rec.module_id}`);
  };

  const runTransition = async (action) => {
    try {
      const { data } = await api.post(`/records/${id}/transition`, { action });
      setRec(data);
      toast.success(`Record ${data.status.replace("_", " ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    }
  };

  if (!rec || !cfg) return <div className="p-8 font-mono-plex text-sm">Loading record…</div>;

  return (
    <div className="p-8 space-y-6" data-testid="record-detail-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Link to={`/app/modules/${rec.module_id}`} className="inline-flex items-center gap-1 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="back-to-module">
            <ArrowLeft className="w-3 h-3" /> {cfg.name} · {recordType?.name}
          </Link>
          <input
            className="mt-3 font-display text-4xl font-bold tracking-tight bg-transparent border-0 outline-none w-full focus:ring-0"
            value={rec.title}
            onChange={(e) => update("title", e.target.value)}
            data-testid="record-title-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="record-actions">
          {(WORKFLOW_ACTIONS[rec.status] || []).map((a) => {
            const AI = a.icon;
            return (
              <Button
                key={a.id}
                variant={a.variant}
                onClick={() => runTransition(a.id)}
                data-testid={`record-action-${a.id}`}
                className={`rounded-sm ${a.variant === "default" ? "bg-primary hover:bg-primary/90" : ""}`}
              >
                <AI className="w-4 h-4 mr-2" /> {a.label}
              </Button>
            );
          })}
          {recordType?.ai && (
            <Button onClick={generate} disabled={generating} data-testid="ai-assist-btn" className="rounded-sm bg-accent hover:bg-accent/90 text-white">
              <Sparkles className="w-4 h-4 mr-2" />
              {generating ? "Drafting…" : "AI Draft"}
            </Button>
          )}
          <Button onClick={save} disabled={!dirty || saving} data-testid="save-record-btn" className="rounded-sm bg-primary hover:bg-primary/90">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
          <a
            href={`/app/records/${id}/print`}
            target="_blank" rel="noreferrer"
            data-testid="print-link"
          >
            <Button variant="outline" className="rounded-sm">
              <Printer className="w-4 h-4 mr-2" /> Print / PDF
            </Button>
          </a>
          <Button variant="ghost" size="icon" onClick={del} data-testid="delete-record-btn">
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Description */}
        <div className="lg:col-span-3 border border-border bg-card p-6">
          <div className="overline text-muted-foreground mb-3">Description</div>
          <Textarea
            rows={20}
            value={rec.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder={recordType?.ai
              ? "Type your notes, or click AI Draft to generate a full document."
              : "Details, notes, links…"}
            data-testid="record-description"
            className="rounded-sm font-mono-plex text-sm leading-relaxed"
          />
        </div>

        {/* Metadata */}
        <aside className="border border-border bg-card p-6 space-y-5 h-fit" data-testid="record-metadata">
          <div>
            <Label>Status</Label>
            <Select value={rec.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="record-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={rec.priority} onValueChange={(v) => update("priority", v)}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="record-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Assignee</Label>
            <Input value={rec.assignee || ""} onChange={(e) => update("assignee", e.target.value)}
              placeholder="Name or email"
              data-testid="record-assignee"
              className="rounded-sm mt-1.5" />
          </div>
          <div>
            <Label>Due date</Label>
            <Input type="date" value={(rec.due_date || "").slice(0, 10)}
              onChange={(e) => update("due_date", e.target.value)}
              data-testid="record-due-date"
              className="rounded-sm mt-1.5" />
          </div>
          <div className="pt-4 border-t border-border text-xs font-mono-plex text-muted-foreground uppercase tracking-widest space-y-1">
            <div>Author · {rec.author_name}</div>
            <div>Created · {new Date(rec.created_at).toLocaleDateString()}</div>
            <div>Updated · {new Date(rec.updated_at).toLocaleDateString()}</div>
          </div>
        </aside>
      </div>

      {/* Comments + Activity */}
      <ActivityPanel entityType="record" entityId={rec.id} />
    </div>
  );
}
